import { useDraggable } from '@dnd-kit/core';
import {
  AppWindow,
  Boxes,
  Columns2,
  HelpCircle,
  Image as ImageIcon,
  LayoutGrid,
  MessageSquareWarning,
  Plus,
  PlaySquare,
  Shapes,
  Sigma,
  Milestone,
  Smile,
  Square,
  Tag,
  Type,
  Video,
  Workflow,
  type LucideIcon,
} from 'lucide-react';

import { useRequestBlankSlide } from '@/components/canvas/BlankSlideInsertContext';
import type { ElementType, RowLayout } from '@/lib/schema.types';
import { cn } from '@/lib/utils';
import { useEditorActions, useEditorStore } from '@/store/editor.store';

interface BlockDef {
  type: ElementType;
  label: string;
  icon: LucideIcon;
  iconClass: string;
}

const BLOCKS: BlockDef[] = [
  { type: 'text', label: 'Text Content', icon: Type, iconClass: 'bg-indigo-100 text-indigo-600' },
  { type: 'image', label: 'Image Asset', icon: ImageIcon, iconClass: 'bg-emerald-100 text-emerald-600' },
  { type: 'video', label: 'Video Player', icon: Video, iconClass: 'bg-rose-100 text-rose-600' },
  { type: 'mcq', label: 'Question', icon: PlaySquare, iconClass: 'bg-amber-100 text-amber-600' },
  { type: 'chip', label: 'Chip / Tag', icon: Tag, iconClass: 'bg-sky-100 text-sky-600' },
  { type: 'shape', label: 'Shape', icon: Shapes, iconClass: 'bg-purple-100 text-purple-600' },
  { type: 'icon', label: 'Icon', icon: Smile, iconClass: 'bg-indigo-100 text-indigo-600' },
  { type: 'callout', label: 'Callout', icon: MessageSquareWarning, iconClass: 'bg-red-100 text-red-600' },
  { type: 'latex', label: 'Math (LaTeX)', icon: Sigma, iconClass: 'bg-orange-100 text-orange-600' },
  { type: 'mermaid', label: 'Mermaid Diagram', icon: Workflow, iconClass: 'bg-teal-100 text-teal-600' },
  { type: 'timeline', label: 'Timeline', icon: Milestone, iconClass: 'bg-lime-100 text-lime-600' },
  { type: 'embed', label: 'Embed / Simulation', icon: AppWindow, iconClass: 'bg-cyan-100 text-cyan-600' },
  { type: '3d', label: '3D Model', icon: Boxes, iconClass: 'bg-fuchsia-100 text-fuchsia-600' },
];

// Each layout is just "how many freeform columns sit side by side". There is no
// separate flow vs freeform — every column is a freeform canvas.
const ROW_LAYOUTS: { layout: RowLayout; label: string; icon: LucideIcon }[] = [
  { layout: 1, label: '1-col', icon: Square },
  { layout: 2, label: '2-col', icon: Columns2 },
  { layout: 3, label: '3-col', icon: LayoutGrid },
];

export function LeftToolbar() {
  const { requestNewSlide } = useRequestBlankSlide();
  const blocks = useVisibleBlocks();

  return (
    <aside className="flex w-[260px] shrink-0 flex-col overflow-hidden border-r border-border bg-bg-panel">
      <div className="flex-1 overflow-y-auto px-3 py-3 scroll-thin">
        <PaneHeading>Drag onto canvas</PaneHeading>
        <div className="space-y-1.5">
          {blocks.map((b) => (
            <DraggableBlock key={b.type} def={b} />
          ))}
        </div>

        <div className="mt-5">
          <PaneHeading>Add row to current slide</PaneHeading>
          <div className="grid grid-cols-3 gap-1.5">
            {ROW_LAYOUTS.map((l) => (
              <QuickLayoutChip key={l.layout} def={l} />
            ))}
          </div>
        </div>

        <PackSlidesSection />
      </div>

      <div className="border-t border-border-subtle p-3">
        <button
          onClick={() => requestNewSlide()}
          className="flex h-10 w-full items-center justify-center gap-2 rounded-md bg-accent text-sm font-semibold text-white shadow-sm transition hover:bg-accent-hover"
        >
          <Plus size={16} /> NEW SLIDE
        </button>
      </div>
    </aside>
  );
}

/** In pack mode the free-floating `mcq` block is hidden — quizzes get their own
 *  quiz slide (SCHEMA_USAGE.md Rule 4). */
function useVisibleBlocks(): BlockDef[] {
  const mode = useEditorStore((s) => s.mode);
  return mode === 'pack' ? BLOCKS.filter((b) => b.type !== 'mcq') : BLOCKS;
}

function PaneHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="mb-2 px-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-fg-subtle">
      {children}
    </h3>
  );
}

function QuickLayoutChip({ def }: { def: { layout: RowLayout; label: string; icon: LucideIcon } }) {
  const insertSlideId = useEditorStore(
    (s) =>
      (s.selection && 'slideId' in s.selection ? s.selection.slideId : null) ??
      s.script?.slides[0]?.id ??
      null,
  );
  const actions = useEditorActions();
  const Icon = def.icon;
  return (
    <button
      onClick={() => insertSlideId && actions.addRow(insertSlideId, def.layout)}
      title={`Add a ${def.label} freeform row to the current slide`}
      className="flex h-8 items-center justify-center gap-1.5 rounded-md border border-border bg-white text-[11px] font-medium text-fg-muted transition hover:border-accent/60 hover:text-accent"
    >
      <Icon size={12} /> {def.label}
    </button>
  );
}

function PackSlidesSection() {
  const mode = useEditorStore((s) => s.mode);
  if (mode !== 'pack') return null;
  return (
    <div className="mt-5">
      <PaneHeading>Pack slides</PaneHeading>
      <QuizSlideCard />
    </div>
  );
}

/**
 * Pack-mode: insert a fully-formed quiz slide (one mcq + `kind:"quiz"`
 * beatMeta). If the selected slide is already a quiz, this chains "add another
 * question" (SCHEMA_USAGE.md Rule 4).
 */
function QuizSlideCard() {
  const actions = useEditorActions();
  const selectedSlide = useEditorStore((s) => {
    const sel = s.selection;
    if (!sel || !('slideId' in sel)) return null;
    return s.script?.slides.find((sl) => sl.id === sel.slideId) ?? null;
  });
  const inQuizSet = selectedSlide?.beatMeta?.kind === 'quiz';

  return (
    <button
      onClick={() =>
        actions.insertQuizSlide(
          inQuizSet && selectedSlide ? { afterSlideId: selectedSlide.id } : undefined,
        )
      }
      title={
        inQuizSet
          ? 'Adds another question to the current quiz set'
          : 'Inserts a single-question quiz slide with branches + thresholds'
      }
      className="group/card flex items-center gap-2.5 rounded-md border border-border bg-white p-2 text-left transition hover:border-accent/60 hover:shadow-sm"
    >
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-amber-100 text-amber-600">
        <HelpCircle size={14} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[12.5px] font-semibold text-fg">
          {inQuizSet ? 'Add another question' : 'Quiz slide'}
        </span>
        <span className="mt-0.5 block text-[10.5px] text-fg-muted">
          {inQuizSet
            ? 'Chains a new quiz slide after the selected one'
            : 'One question · branches · thresholds'}
        </span>
      </span>
    </button>
  );
}

function DraggableBlock({ def }: { def: BlockDef }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `toolbar-element-${def.type}`,
    data: { source: 'toolbar', kind: 'element', elementType: def.type },
  });
  const Icon = def.icon;
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={cn(
        'group/block flex cursor-grab items-center gap-3 rounded-md border border-border bg-white px-3 py-2 transition hover:border-accent/40 hover:shadow-sm active:cursor-grabbing',
        isDragging && 'opacity-40',
      )}
      title={`Drag ${def.label} onto a canvas`}
    >
      <span className={cn('grid h-7 w-7 place-items-center rounded-md', def.iconClass)}>
        <Icon size={14} />
      </span>
      <span className="text-sm font-medium text-fg">{def.label}</span>
    </div>
  );
}
